import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints.ts';

import {
  CreatePatientDto,
  CreatePatientResponse,
  PatientListRequestParams,
  PatientListResponse
} from '@/types/patientList';

import { PreviousReportFilters } from '../../patientList/patientListFiltersSlice';

interface TransmissionReportIdsRequest {
  patientIds: string[];
}

interface TransmissionReportIdsResponse {
  [patientId: string]: number[];
}

interface CurrentReportFilters {
  colors?: string[];
  isNonPractitionerSigned?: true;
  isPendingReview?: true;
}

interface NotDownloadedReportsFilters {
  clinicIds?: string[];
  practitionersIds?: string[];
  deviceTypes?: string[];
  previousReportFilters?: PreviousReportFilters;
  currentReportFilters?: CurrentReportFilters;
  vendors?: string[];
  patientStatuses?: string[];
}

type NotDownloadedReportsResponse = number[];

export const patientListApiSlice = createApi({
  reducerPath: 'patientListApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [API_SLICE.PatientList],
  endpoints: (builder) => ({
    getAllPatients: builder.query<PatientListResponse, PatientListRequestParams>({
      query: (params) => ({
        url: API_ENDPOINTS.PATIENTS.LIST_V2,
        method: 'POST',
        body: params
      }),
      providesTags: (_result, _error, _params) => [{ type: API_SLICE.PatientList, id: 'LIST' }]
    }),
    getTransmissionReportIds: builder.query<
      TransmissionReportIdsResponse,
      TransmissionReportIdsRequest
    >({
      query: (params) => ({
        url: '/patients/v2/transmission-report-ids',
        method: 'POST',
        body: params
      }),
      providesTags: (_result, _error, params) => [
        { type: API_SLICE.PatientList, id: `TRANSMISSION_REPORTS_${params.patientIds.join('_')}` }
      ]
    }),
    getNotDownloadedReports: builder.query<
      NotDownloadedReportsResponse,
      NotDownloadedReportsFilters
    >({
      query: (filters = {}) => ({
        url: '/patients/v2/transmission-reports/not-downloaded',
        method: 'POST',
        body: {
          clinicIds: filters.clinicIds,
          practitionersIds: filters.practitionersIds,
          deviceTypes: filters.deviceTypes,
          previousReportFilters: filters.previousReportFilters,
          currentReportFilters: filters.currentReportFilters,
          vendors: filters.vendors,
          patientStatuses: filters.patientStatuses
        }
      }),
      providesTags: [{ type: API_SLICE.PatientList, id: 'NOT_DOWNLOADED_REPORTS' }]
    }),
    createPatient: builder.mutation<CreatePatientResponse, CreatePatientDto>({
      query: (params) => ({
        url: '/patients/v2/create-rmg-patient',
        method: 'POST',
        body: params
      })
    })
  })
});

export const {
  useGetAllPatientsQuery,
  useGetTransmissionReportIdsQuery,
  useLazyGetTransmissionReportIdsQuery,
  useGetNotDownloadedReportsQuery,
  useCreatePatientMutation
} = patientListApiSlice;
